import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { Appointment } from "models";

type CreatePhoneNumber = {
  id: number;
  description: string;
  phoneNr?: string;
};

type PhoneOption = {
  key: number;
  text: string;
  value: string;
  description: string;
};

export const useAppointmentNotePhoneOptions = (appointment: Appointment) => {
  const { t } = useTranslation();

  const phoneOptions = useMemo(() => {
    const options: PhoneOption[] = [];

    const addPhoneOption = ({ id, phoneNr, description }: CreatePhoneNumber) =>
      phoneNr && options.push({ key: options.length + 1, text: phoneNr, value: `${phoneNr}-${id}-${options.length}`, description });

    if (appointment.customer_driver?.id) {
      const { id, tel_business_nr, tel_mobile_business, tel_mobile_private, tel_private_nr } = appointment.customer_driver;
      addPhoneOption({ id, phoneNr: tel_mobile_business, description: t("v8_driver_mobile_business", "Driver Mobile Business") });
      addPhoneOption({ id, phoneNr: tel_mobile_private, description: t("v8_driver_mobile_private", "Driver Mobile Private") });
      addPhoneOption({ id, phoneNr: tel_business_nr, description: t("v8_driver_business", "Driver Business") });
      addPhoneOption({ id, phoneNr: tel_private_nr, description: t("v8_driver_private", "Driver Private") });
    }

    if (appointment.customer_contract?.id) {
      const { id, tel_business_nr, tel_mobile_business, tel_mobile_private, tel_private_nr } = appointment.customer_contract;
      addPhoneOption({ id, phoneNr: tel_mobile_business, description: t("v8_contract_mobile_business", "Contract Mobile Business") });
      addPhoneOption({ id, phoneNr: tel_mobile_private, description: t("v8_contract_mobile_private", "Contract Mobile Private") });
      addPhoneOption({ id, phoneNr: tel_business_nr, description: t("v8_contract_business", "Contract Business") });
      addPhoneOption({ id, phoneNr: tel_private_nr, description: t("v8_contract_private", "Contract Private") });
    }

    if (appointment.customer_owner?.id) {
      const { id, tel_business_nr, tel_mobile_business, tel_mobile_private, tel_private_nr } = appointment.customer_owner;
      addPhoneOption({ id, phoneNr: tel_mobile_business, description: t("v8_owner_mobile_business", "Owner Mobile Business") });
      addPhoneOption({ id, phoneNr: tel_mobile_private, description: t("v8_owner_mobile_private", "Owner Mobile Private") });
      addPhoneOption({ id, phoneNr: tel_business_nr, description: t("v8_owner_business", "Owner Business") });
      addPhoneOption({ id, phoneNr: tel_private_nr, description: t("v8_owner_private", "Owner Private") });
    }

    options.push({ key: options.length + 1, value: "other", text: t("v8_other", "Other"), description: "" });

    return options;
  }, [appointment]);

  return { phoneOptions };
};
